"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  buildSearchSuggestions,
  findMatchingEngines,
  getBaseModels,
  getBrands,
  getDisplacements,
  getFuelTypes,
  getPowers,
  loadDatabase,
} from "@/lib/database";
import type {
  CatalogItem,
  CategorySlug,
  SearchSuggestion,
  SelectedFilters,
} from "@/lib/types";

const PAGE_SIZE = 12;

const emptyFilters: SelectedFilters = {
  brand: "",
  model: "",
  fuel: "",
  displacement: "",
  power: "",
  searchQuery: "",
};

interface UseCatalogSelectorOptions {
  initialCatalog?: CatalogItem[];
  initialError?: string | null;
}

export function useCatalogSelector(options: UseCatalogSelectorOptions = {}) {
  const hasInitialCatalog = (options.initialCatalog?.length ?? 0) > 0;
  const [db, setDb] = useState<CatalogItem[]>(options.initialCatalog ?? []);
  const [loading, setLoading] = useState(
    !hasInitialCatalog && !options.initialError,
  );
  const [error, setError] = useState<string | null>(
    options.initialError ?? null,
  );
  const [selectedCategory, setSelectedCategory] = useState<CategorySlug>("all");
  const [selected, setSelected] = useState<SelectedFilters>(emptyFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeStep, setActiveStep] = useState(1);

  const reloadDatabase = useCallback(() => {
    setLoading(true);
    setError(null);
    loadDatabase()
      .then((data) => {
        setDb(data);
        setError(null);
      })
      .catch((err: unknown) => {
        setDb([]);
        setError(
          err instanceof Error
            ? err.message
            : "Не удалось загрузить каталог агрегатов",
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (hasInitialCatalog || options.initialError) {
      return;
    }
    reloadDatabase();
  }, [hasInitialCatalog, options.initialError, reloadDatabase]);

  const brands = useMemo(
    () => getBrands(db, selectedCategory),
    [db, selectedCategory],
  );

  const baseModels = useMemo(
    () =>
      selected.brand
        ? getBaseModels(db, selected.brand, selectedCategory)
        : [],
    [db, selected.brand, selectedCategory],
  );

  const fuels = useMemo(
    () =>
      selected.brand && selected.model
        ? getFuelTypes(db, selected.brand, selected.model, selectedCategory)
        : [],
    [db, selected.brand, selected.model, selectedCategory],
  );

  const displacements = useMemo(
    () =>
      selected.brand && selected.model && selected.fuel
        ? getDisplacements(
            db,
            selected.brand,
            selected.model,
            selected.fuel,
            selectedCategory,
          )
        : [],
    [db, selected.brand, selected.model, selected.fuel, selectedCategory],
  );

  const powers = useMemo(
    () =>
      selected.brand &&
      selected.model &&
      selected.fuel &&
      selected.displacement
        ? getPowers(
            db,
            selected.brand,
            selected.model,
            selected.fuel,
            selected.displacement,
            selectedCategory,
          )
        : [],
    [db, selected.brand, selected.model, selected.fuel, selected.displacement, selectedCategory],
  );

  const matchedItems = useMemo(
    () =>
      findMatchingEngines(db, {
        categorySlug: selectedCategory,
        ...selected,
      }),
    [db, selectedCategory, selected],
  );

  const totalPages = Math.max(1, Math.ceil(matchedItems.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedItems = matchedItems.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (selected.brand) parts.push(`Марка: ${selected.brand}`);
    if (selected.model) parts.push(`Модель: ${selected.model}`);
    if (selected.fuel) parts.push(`Топливо: ${selected.fuel}`);
    if (selected.displacement) parts.push(`Объем: ${selected.displacement}`);
    if (selected.power) parts.push(`Мощность: ${selected.power}`);
    if (selected.searchQuery) parts.push(`Поиск: "${selected.searchQuery}"`);
    return parts.length > 0
      ? `Результаты подбора: ${parts.join(" • ")}`
      : "Показаны все доступные ДВС и КПП";
  }, [selected]);

  const clearWizardFilters = useCallback(() => {
    setSelected((prev) => ({
      ...prev,
      brand: "",
      model: "",
      fuel: "",
      displacement: "",
      power: "",
    }));
    setActiveStep(1);
  }, []);

  const clearSearch = useCallback(() => {
    setSelected((prev) => ({ ...prev, searchQuery: "" }));
    setSuggestions([]);
    setShowSuggestions(false);
  }, []);

  const onCategoryChange = useCallback((category: CategorySlug) => {
    setSelectedCategory(category);
    setSelected(emptyFilters);
    setCurrentPage(1);
    setActiveStep(1);
    setSuggestions([]);
    setShowSuggestions(false);
  }, []);

  const onBrandChange = useCallback(
    (brand: string, scrollStep = false) => {
      if (!brand) {
        setSelected(emptyFilters);
        setCurrentPage(1);
        setActiveStep(1);
        return;
      }
      setSelected((prev) => ({
        ...prev,
        brand,
        model: "",
        fuel: "",
        displacement: "",
        power: "",
      }));
      setCurrentPage(1);
      setActiveStep(2);
      if (scrollStep) {
        setTimeout(() => {
          document.getElementById("stepBlock2")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 150);
      }
    },
    [],
  );

  const onModelChange = useCallback((model: string, scrollStep = false) => {
    if (!model) {
      setSelected((prev) => ({
        ...prev,
        model: "",
        fuel: "",
        displacement: "",
        power: "",
      }));
      setActiveStep(2);
      return;
    }
    setSelected((prev) => ({
      ...prev,
      model,
      fuel: "",
      displacement: "",
      power: "",
    }));
    setCurrentPage(1);
    setActiveStep(3);
    if (scrollStep) {
      setTimeout(() => {
        document.getElementById("stepBlock3")?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 150);
    }
  }, []);

  const onFuelChange = useCallback((fuel: string, scrollStep = false) => {
    if (!fuel) {
      setSelected((prev) => ({
        ...prev,
        fuel: "",
        displacement: "",
        power: "",
      }));
      setActiveStep(3);
      return;
    }
    setSelected((prev) => ({
      ...prev,
      fuel,
      displacement: "",
      power: "",
    }));
    setCurrentPage(1);
    setActiveStep(4);
    if (scrollStep) {
      setTimeout(() => {
        document.getElementById("stepBlock4")?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 150);
    }
  }, []);

  const onDisplacementChange = useCallback(
    (displacement: string, scrollStep = false) => {
      if (!displacement) {
        setSelected((prev) => ({
          ...prev,
          displacement: "",
          power: "",
        }));
        setActiveStep(4);
        return;
      }
      setSelected((prev) => ({
        ...prev,
        displacement,
        power: "",
      }));
      setCurrentPage(1);
      setActiveStep(5);
      if (scrollStep) {
        setTimeout(() => {
          document.getElementById("stepBlock5")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 150);
      }
    },
    [],
  );

  const onPowerChange = useCallback((power: string, scrollStep = false) => {
    setSelected((prev) => ({ ...prev, power }));
    setCurrentPage(1);
    if (scrollStep) {
      setTimeout(() => {
        document.getElementById("resultsSection")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);
    }
  }, []);

  const handleSearchInput = useCallback(
    (query: string) => {
      setSelected((prev) => ({ ...prev, searchQuery: query }));
      setCurrentPage(1);
      const newSuggestions = buildSearchSuggestions(db, query);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    },
    [db],
  );

  const submitFreeTextSearch = useCallback(
    (query: string) => {
      clearWizardFilters();
      setSelectedCategory("all");
      setSelected({ ...emptyFilters, searchQuery: query });
      setCurrentPage(1);
      setShowSuggestions(false);
      setTimeout(() => {
        document.getElementById("resultsSection")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    },
    [clearWizardFilters],
  );

  const applySuggestion = useCallback(
    (suggestion: SearchSuggestion) => {
      if (suggestion.type === "model" && suggestion.brand && suggestion.model) {
        clearSearch();
        setSelectedCategory((suggestion.categorySlug as CategorySlug) || "all");
        onBrandChange(suggestion.brand, false);
        onModelChange(suggestion.model, true);
      } else {
        submitFreeTextSearch(suggestion.value);
      }
      setShowSuggestions(false);
    },
    [clearSearch, onBrandChange, onModelChange, submitFreeTextSearch],
  );

  const resetAll = useCallback(() => {
    setSelected(emptyFilters);
    setSelectedCategory("all");
    setCurrentPage(1);
    setActiveStep(1);
    setSuggestions([]);
    setShowSuggestions(false);
  }, []);

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
    document.getElementById("resultsSection")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  return {
    loading,
    error,
    reloadDatabase,
    db,
    selectedCategory,
    selected,
    brands,
    baseModels,
    fuels,
    displacements,
    powers,
    matchedItems,
    paginatedItems,
    currentPage: safePage,
    totalPages,
    filterSummary,
    suggestions,
    showSuggestions,
    setShowSuggestions,
    activeStep,
    onCategoryChange,
    onBrandChange,
    onModelChange,
    onFuelChange,
    onDisplacementChange,
    onPowerChange,
    handleSearchInput,
    submitFreeTextSearch,
    applySuggestion,
    resetAll,
    goToPage,
    clearSearch,
    setSelected,
  };
}
